import ImprovedAnalyticsService from './ImprovedAnalyticsService';
import sessionDeduplicationService from './SessionDeduplicationService';
import realTimeAnalytics from './RealTimeAnalyticsAggregator';

class AnalyticsManager {
  constructor() {
    this.isInitialized = false;
    this.services = {
      analytics: ImprovedAnalyticsService,
      deduplication: sessionDeduplicationService,
      realTime: realTimeAnalytics
    };
  }

  // Initialize all analytics services
  async init() {
    if (this.isInitialized) {
      console.log('Analytics manager already initialized');
      return;
    }

    try {
      console.log('Initializing analytics manager...');

      // Initialize improved analytics service
      await this.services.analytics.initAnalytics();
      console.log('✓ Improved analytics service initialized');

      // Initialize real-time analytics aggregator
      await this.services.realTime.init();
      console.log('✓ Real-time analytics aggregator initialized');

      // Start session deduplication service
      this.services.deduplication.start();
      console.log('✓ Session deduplication service started');

      this.isInitialized = true;
      console.log('Analytics manager initialized successfully');

    } catch (error) {
      console.error('Error initializing analytics manager:', error);
      throw error;
    }
  }

  // Track page view using the improved service
  async trackPageView(pageName, pageParams = {}) {
    try {
      if (!this.isInitialized) {
        console.warn('Analytics manager not initialized, initializing now...');
        await this.init();
      }

      console.log(`📊 Tracking page view: "${pageName}"`, pageParams);
      await this.services.analytics.trackPageView(pageName, pageParams);
      console.log(`✅ Page view "${pageName}" tracked successfully`);
    } catch (error) {
      console.error(`❌ Error tracking page view "${pageName}":`, error);
    }
  }

  // Track user signup
  async trackUserSignup(userId, userData) {
    try {
      if (!this.isInitialized) {
        console.warn('Analytics manager not initialized, initializing now...');
        await this.init();
      }

      await this.services.analytics.trackUserSignup(userId, userData);
    } catch (error) {
      console.error('Error tracking user signup:', error);
    }
  }

  // Track registry item events
  async trackRegistryItem(eventType, itemData, userId = null) {
    try {
      if (!this.isInitialized) {
        console.warn('Analytics manager not initialized, initializing now...');
        await this.init();
      }

      console.log(`📊 Tracking registry item: "${eventType}" for item "${itemData?.name || 'unknown'}"`, itemData);
      await this.services.analytics.trackRegistryItem(eventType, itemData, userId);
      console.log(`✅ Registry item "${eventType}" tracked successfully for "${itemData?.name || 'unknown'}"`);
    } catch (error) {
      console.error(`❌ Error tracking registry item "${eventType}":`, error);
    }
  }

  // Get analytics data using the improved service
  getAnalyticsData() {
    return this.services.analytics.getAnalyticsData;
  }

  // Get real-time analytics data
  getRealTimeData() {
    return this.services.realTime.getCurrentData();
  }

  // Subscribe to real-time updates
  subscribeToRealTime(callback) {
    if (!this.isInitialized) {
      console.warn('Analytics manager not initialized');
      return () => {};
    }

    return this.services.realTime.subscribe(callback);
  }

  // Get deduplication statistics
  async getDeduplicationStats() {
    try {
      return await this.services.deduplication.getDeduplicationStats();
    } catch (error) {
      console.error('Error getting deduplication stats:', error);
      return null;
    }
  }

  // Force a deduplication run
  async runDeduplication() {
    try {
      await this.services.deduplication.deduplicateSessions();
      console.log('Manual deduplication completed');
    } catch (error) {
      console.error('Error running manual deduplication:', error);
    }
  }

  // Get comprehensive analytics health status
  async getHealthStatus() {
    try {
      const deduplicationStats = await this.getDeduplicationStats();
      const realTimeData = this.getRealTimeData();

      return {
        isInitialized: this.isInitialized,
        services: {
          analytics: true, // Always true if manager is initialized
          realTime: realTimeData.lastUpdated ? true : false,
          deduplication: deduplicationStats ? true : false
        },
        stats: {
          ...deduplicationStats,
          realTimeLastUpdate: realTimeData.lastUpdated
        },
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Error getting health status:', error);
      return {
        isInitialized: this.isInitialized,
        services: {
          analytics: false,
          realTime: false,
          deduplication: false
        },
        error: error.message,
        timestamp: new Date()
      };
    }
  }

  // Cleanup all services
  cleanup() {
    try {
      console.log('Cleaning up analytics manager...');

      // Stop session deduplication
      this.services.deduplication.stop();
      
      // Cleanup real-time analytics
      this.services.realTime.cleanup();
      
      // Cleanup improved analytics service
      if (this.services.analytics.cleanup) {
        this.services.analytics.cleanup();
      }

      this.isInitialized = false;
      console.log('Analytics manager cleanup completed');
    } catch (error) {
      console.error('Error during analytics manager cleanup:', error);
    }
  }

  // Force refresh of all analytics data
  async refreshAll() {
    try {
      console.log('Refreshing all analytics data...');

      // Run deduplication
      await this.runDeduplication();

      // Update real-time aggregated data
      await this.services.realTime.updateAggregatedData();

      console.log('Analytics data refresh completed');
    } catch (error) {
      console.error('Error refreshing analytics data:', error);
    }
  }
}

// Export singleton instance
export const analyticsManager = new AnalyticsManager();
export default analyticsManager;